import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import app from '@/firebase';
import { isTrialWeekOptionCEnabled } from '@/config/featureFlags';
import { createFunctions, ensureFunctionsEmulator } from '@/utils/firebase/emulator';
import { logger } from '@/utils/logger';
import { parseTrialPlan } from './parseTrialPlan';
import type {
  GetTrialPlanRequest,
  StartTrialBlockRequest,
  StartTrialBlockResponse,
  TrialPlan,
} from './types';

export const trialPlanKeys = {
  all: ['trial-plan'] as const,
};

function callable<Req extends object, Res>(name: string) {
  const functions = createFunctions(app);
  try {
    ensureFunctionsEmulator(functions);
  } catch {
    // Emulator wiring is best-effort; the callable still resolves against prod.
  }
  return httpsCallable<Req, Res>(functions, name);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Null when the backend has the trial week off or this learner has no plan. */
async function fetchTrialPlan(): Promise<TrialPlan | null> {
  // Local preview only. Never set in prod; keeps UI walkable without WP1.
  if (import.meta.env.VITE_TRIAL_WEEK_FIXTURE === 'true') {
    const { loadPreviewTrialPlan } = await import('./previewPlan');
    return loadPreviewTrialPlan();
  }

  const result = await callable<GetTrialPlanRequest, unknown>('getTrialPlan')({});
  const data = result.data;

  const plan = parseTrialPlan(data);
  if (plan) return plan;
  if (isRecord(data) && typeof data.enabled === 'boolean') return null;

  logger.warn('useTrialPlan', 'Unrecognized getTrialPlan payload', { data });
  throw new Error('Unrecognized getTrialPlan payload');
}

export function useTrialPlan({ enabled = true }: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: trialPlanKeys.all,
    queryFn: fetchTrialPlan,
    enabled: enabled && isTrialWeekOptionCEnabled(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  return { ...query, data: query.data ?? undefined };
}

/**
 * Transport failures: the callable never reached a verdict (network down, not
 * deployed, timed out). Every other code is the server answering "no", and a
 * caller must not paper over it with a local fallback.
 */
const TRANSPORT_CODES = new Set(['internal', 'unavailable', 'deadline-exceeded', 'not-found', 'cancelled']);

/** Server refusals identify themselves by message when the code is missing. */
const REFUSAL_MESSAGE =
  /^(no-trial-plan|trial-week-ended|block-empty:|active-session-exists|no-open-module|trial-modules-disabled|exam-date-required)/;

/** The callable SDK code without its `functions/` prefix. */
export function callableErrorCode(error: unknown): string | undefined {
  const code = (error as { code?: string } | null)?.code;
  return typeof code === 'string' ? code.replace(/^functions\//, '') : undefined;
}

/** True when the server answered and refused, whatever its reason. */
export function isServerRefusal(error: unknown): boolean {
  const code = callableErrorCode(error);
  if (code) return !TRANSPORT_CODES.has(code);
  return REFUSAL_MESSAGE.test(error instanceof Error ? error.message : String(error));
}

export function useStartTrialBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ day, mode, blockId, moduleId }: StartTrialBlockRequest): Promise<string> => {
      // Send the optional keys only when they have a value. The callable SDK
      // encodes `undefined` as null and the server takes `blockId?: string`, so a
      // review launch — which never carries a block — was rejected as
      // "blockId: Expected string, received null" before any trial rule ran.
      const request: StartTrialBlockRequest = {
        day,
        mode,
        ...(blockId ? { blockId } : {}),
        ...(moduleId ? { moduleId } : {}),
      };
      const result = await callable<StartTrialBlockRequest, StartTrialBlockResponse>(
        'startTrialBlock',
      )(request);
      const sessionId = result.data?.sessionId;
      if (!sessionId) throw new Error('startTrialBlock returned no sessionId');
      return sessionId;
    },
    // App.tsx retries every mutation once. A refusal is deterministic, so that
    // retry only doubled the 400s; a transport blip still gets its second try.
    retry: (failureCount, error) => failureCount < 1 && !isServerRefusal(error),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trialPlanKeys.all });
    },
  });
}
